import { useTheme } from "@/hooks/useTheme";

const steps = [
  {
    number: "01",
    label: "Capture",
    icon: "ri-radar-line",
    description: "Every inbound signal — call, form, message — is captured and logged automatically with zero manual input required.",
  },
  {
    number: "02",
    label: "Respond",
    icon: "ri-send-plane-line",
    description: "An instant, personalized response is triggered within seconds via voice, SMS, or chat before your competitor sees the lead.",
  },
  {
    number: "03",
    label: "Qualify",
    icon: "ri-shield-check-line",
    description: "The system scores and filters each lead based on your criteria, surfacing only the highest-value opportunities to your team.",
  },
  {
    number: "04",
    label: "Convert",
    icon: "ri-trophy-line",
    description: "Qualified leads are routed, booked, and handed off with full context — ready to close with no friction or gaps.",
  },
];

export default function ProcessSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#0d0818" : "white";
  const headingColor = isDark ? "#e4d0f8" : "#4d316c";
  const subheadingColor = isDark ? "#c4aad8" : "#705a89";
  const labelColor = isDark ? "#a9baab" : "#869b8b";
  const imageBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const lightCardBg = isDark ? "#1a0d28" : "#faf8f5";
  const lightCardBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const lightCardHeading = isDark ? "#e4d0f8" : "#4d316c";
  const lightCardBody = isDark ? "rgba(255,255,255,0.55)" : "#7a6b80";
  const lightCardIconBg = isDark ? "rgba(169,186,171,0.12)" : "#f0e9de";
  const noteBg = isDark ? "rgba(255,255,255,0.04)" : "#f0e9de";
  const noteBorder = isDark ? "rgba(255,255,255,0.08)" : "#e4d8c8";
  const noteText = isDark ? "rgba(255,255,255,0.5)" : "#5a5060";
  const lightGiantNum = isDark ? "rgba(255,255,255,0.05)" : "#e4d8c8";

  return (
    <section id="process" className="py-28 md:py-40 overflow-hidden transition-colors duration-300" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-24">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
            The Process
          </span>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight" style={{ color: headingColor }}>
            From Lead to Revenue —
            <br />
            <span style={{ color: subheadingColor }}>Fully Systemized.</span>
          </h2>
        </div>

        {/* Large visual */}
        <div
          className="relative w-full rounded-3xl overflow-hidden mb-20"
          style={{ border: `1px solid ${imageBorder}` }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/5a459efcd0891ca4c24792f0100de1fb.jpg"
            alt="Lead to revenue process flow"
            className="w-full object-cover object-center"
            style={{ height: "320px", maxHeight: "380px" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-end justify-around pb-8 px-8">
            {steps.map((step) => (
              <div key={step.label} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full"
                  style={{ background: "rgba(77,49,108,0.9)", backdropFilter: "blur(8px)" }}
                >
                  <i className={`${step.icon} text-sm`} style={{ color: "#a9baab" }}></i>
                </div>
                <span className="text-xs font-semibold text-white/90 hidden sm:block">{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step detail cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, idx) => {
            const isDarkCard = idx % 2 === 0;
            return (
              <div
                key={step.label}
                className="relative p-7 rounded-2xl overflow-hidden"
                style={{
                  background: isDarkCard ? "#4d316c" : lightCardBg,
                  border: `1px solid ${isDarkCard ? "transparent" : lightCardBorder}`,
                }}
              >
                <span
                  className="absolute -top-4 -right-2 text-8xl font-bold leading-none select-none pointer-events-none"
                  style={{ color: isDarkCard ? "rgba(255,255,255,0.06)" : lightGiantNum }}
                >
                  {step.number}
                </span>

                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-5 relative z-10"
                  style={{ background: isDarkCard ? "rgba(169,186,171,0.2)" : lightCardIconBg }}
                >
                  <i className={`${step.icon} text-lg`} style={{ color: isDarkCard ? "#a9baab" : (isDark ? "#c4aad8" : "#4d316c") }}></i>
                </div>

                <h3
                  className="text-lg font-semibold mb-3 relative z-10"
                  style={{ color: isDarkCard ? "white" : lightCardHeading }}
                >
                  {step.label}
                </h3>
                <p
                  className="text-sm leading-relaxed relative z-10"
                  style={{ color: isDarkCard ? "rgba(255,255,255,0.65)" : lightCardBody }}
                >
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div
          className="mt-10 text-center py-5 px-8 rounded-2xl"
          style={{ background: noteBg, border: `1px solid ${noteBorder}` }}
        >
          <p className="text-sm font-medium" style={{ color: noteText }}>
            Every step is automated, tracked, and optimized — giving you complete visibility from first contact to closed deal.
          </p>
        </div>
      </div>
    </section>
  );
}
